if (!customElements.get('featured-reviews-summary')) {
  customElements.define(
    'featured-reviews-summary',
    class FeaturedReviewsSummary extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;
        this.abortController = new AbortController();
        const { signal } = this.abortController;

        this.cards = this.querySelector('[data-featured-cards]');
        this.dialog = this.querySelector('[data-featured-video-dialog]');
        this.dialogMedia = this.querySelector('[data-featured-dialog-media]');
        this.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

        this.addEventListener('click', this.handleClick.bind(this), { signal });
        this.addEventListener('keydown', this.handleKeydown.bind(this), { signal });
        this.dialog?.addEventListener('cancel', this.handleDialogCancel.bind(this), { signal });
        this.dialog?.addEventListener('close', this.handleDialogClose.bind(this), { signal });
        document.addEventListener('shopify:section:load', this.handleSectionLoad.bind(this), { signal });

        this.syncDestination();
        this.syncReviewData();
        this.setupTextLimits();

        if ('ResizeObserver' in window) {
          this.resizeObserver = new ResizeObserver(() => this.setupTextLimits());
          this.resizeObserver.observe(this);
        }
      }

      disconnectedCallback() {
        this.abortController?.abort();
        this.resizeObserver?.disconnect();
        this.reviewObserver?.disconnect();
        if (this.dialog?.open) this.dialog.close();
        document.body.classList.remove('featured-reviews-dialog-open');
        this.initialized = false;
      }

      handleSectionLoad(event) {
        if (!event.target?.querySelector?.('customer-reviews') && !event.target?.matches?.('customer-reviews')) return;
        this.syncReviewData();
        this.syncDestination();
      }

      syncReviewData() {
        const reviewsSection = document.querySelector('customer-reviews');
        if (reviewsSection && reviewsSection !== this.reviewsSection) {
          this.reviewObserver?.disconnect();
          this.reviewsSection = reviewsSection;
          this.reviewObserver = new MutationObserver(() => {
            if (this.reviewSyncScheduled) return;
            this.reviewSyncScheduled = true;
            requestAnimationFrame(() => {
              this.reviewSyncScheduled = false;
              this.syncAggregate(reviewsSection);
              this.syncExistingCards(reviewsSection);
              this.setupTextLimits();
            });
          });
          this.reviewObserver.observe(reviewsSection, {
            attributes: true,
            attributeFilter: ['data-average-rating', 'data-review-count'],
            childList: true,
            subtree: true,
          });
        }

        this.syncAggregate(reviewsSection);
        this.syncExistingCards(reviewsSection);
      }

      syncAggregate(reviewsSection) {
        if (this.dataset.aggregateSource !== 'automatic') return;
        const fullRating = Number(reviewsSection?.dataset.averageRating || 0);
        const fullCount = Number(reviewsSection?.dataset.reviewCount || 0);
        if (fullRating > 0 && fullCount > 0) this.updateAggregate(fullRating, fullCount);
      }

      updateAggregate(rating, count) {
        const aggregate = this.querySelector('[data-featured-aggregate]');
        if (!aggregate || rating <= 0 || count <= 0) return;
        aggregate.hidden = false;
        const roundedRating = Math.round(rating * 10) / 10;
        const label = this.dataset.countLabel || 'reviews';
        const stars = this.querySelector('[data-featured-aggregate-stars]');
        stars?.style.setProperty('--rating-percent', `${Math.min(100, Math.max(0, rating * 20))}%`);
        const average = this.querySelector('[data-featured-average]');
        if (average) average.textContent = roundedRating.toFixed(1);
        const countElement = this.querySelector('[data-featured-count]');
        if (countElement) countElement.textContent = `${count} ${label}`;
        const accessibleLabel = this.querySelector('[data-featured-aggregate-label]');
        if (accessibleLabel) {
          accessibleLabel.textContent = `${roundedRating.toFixed(1)} out of 5 stars, based on ${count} ${label}`;
        }
        const viewCount = this.querySelector('[data-featured-view-count]');
        if (viewCount) {
          viewCount.textContent = `(${count})`;
          viewCount.hidden = false;
        }
      }

      syncExistingCards(reviewsSection) {
        this.querySelectorAll('[data-featured-card][data-source-mode="existing"]').forEach((card) => {
          const reviewId = card.dataset.reviewId?.trim();
          if (!reviewId || !reviewsSection) {
            this.showMissingReviewEditorState(card);
            return;
          }
          const escapedId = CSS.escape(reviewId);
          const source =
            reviewsSection.querySelector(`[data-review-item][data-review-id="${escapedId}"]`) ||
            reviewsSection.querySelector(`[data-review-item][data-original-index="${escapedId}"]`);
          if (!source) {
            this.showMissingReviewEditorState(card);
            return;
          }
          this.populateExistingCard(card, source);
        });
      }

      showMissingReviewEditorState(card) {
        card.hidden = true;
        if (!window.Shopify?.designMode) return;
        card.hidden = false;
        card.classList.add('featured-reviews-summary__card--editor-missing');
        const media = card.querySelector('[data-featured-media]');
        if (media) media.hidden = true;
        const content = card.querySelector('.featured-reviews-summary__content');
        if (content && !content.querySelector('[data-featured-missing]')) {
          const message = document.createElement('p');
          message.dataset.featuredMissing = '';
          message.textContent = 'No matching existing review was found. Check the review ID or use manual genuine-review fields.';
          content.append(message);
        }
      }

      populateExistingCard(card, source) {
        card.hidden = false;
        card.classList.remove('featured-reviews-summary__card--editor-missing');
        card.querySelector('[data-featured-missing]')?.remove();
        const rating = Number(source.dataset.rating || 0);
        const ratingElement = card.querySelector('[data-featured-rating]');
        if (ratingElement && rating > 0) {
          ratingElement.hidden = false;
          ratingElement.setAttribute('aria-label', `${rating} out of 5 stars`);
          ratingElement.style.setProperty('--rating-percent', `${Math.min(100, rating * 20)}%`);
        } else if (ratingElement) {
          ratingElement.hidden = true;
        }

        const sourceTitle = source.querySelector('.customer-reviews__review-title');
        const targetTitle = card.querySelector('[data-featured-title]');
        if (targetTitle) {
          targetTitle.textContent = sourceTitle?.textContent.trim() || '';
          targetTitle.hidden = this.dataset.showReviewTitles !== 'true' || !targetTitle.textContent;
        }

        const sourceBody = source.querySelector('.customer-reviews__review-body');
        const targetBody = card.querySelector('[data-featured-review-text]');
        if (targetBody) targetBody.innerHTML = sourceBody?.innerHTML || '';

        const sourceName = source.querySelector('.customer-reviews__review-meta strong');
        const targetName = card.querySelector('[data-featured-reviewer-name]');
        if (targetName) {
          targetName.textContent = sourceName?.textContent.trim() || '';
          targetName.hidden = this.dataset.showReviewerNames !== 'true' || !targetName.textContent;
        }

        const sourceDate = source.querySelector('.customer-reviews__review-meta time');
        const targetDate = card.querySelector('[data-featured-review-date]');
        if (targetDate) {
          targetDate.textContent = sourceDate?.textContent.trim() || source.dataset.date || '';
          targetDate.dateTime = sourceDate?.getAttribute('datetime') || source.dataset.date || '';
          targetDate.hidden = this.dataset.showReviewDates !== 'true' || !targetDate.textContent;
        }

        const targetVerified = card.querySelector('[data-featured-verified]');
        if (targetVerified) {
          const verified = source.dataset.verified === 'true';
          targetVerified.hidden = this.dataset.showVerifiedBadges !== 'true' || !verified;
        }
        const reviewer = card.querySelector('[data-featured-reviewer]');
        if (reviewer) {
          reviewer.hidden = Array.from(
            reviewer.querySelectorAll('[data-featured-reviewer-name], [data-featured-review-date], [data-featured-verified]')
          ).every((element) => element.hidden);
        }

        this.populateExistingMedia(card, source);
        this.populateExistingProduct(card, source);
      }

      populateExistingMedia(card, source) {
        const target = card.querySelector('[data-featured-media]');
        if (!target) return;
        target.replaceChildren();
        const sourceImage = source.querySelector('[data-review-image] img');
        const sourceVideoButton = source.querySelector('[data-review-video]');

        if (sourceImage) {
          const image = document.createElement('img');
          image.className = 'featured-reviews-summary__media-image';
          image.src = sourceImage.currentSrc || sourceImage.src;
          if (sourceImage.srcset) image.srcset = sourceImage.srcset;
          image.sizes = '(min-width: 990px) 33vw, 88vw';
          image.width = Number(sourceImage.getAttribute('width') || sourceImage.naturalWidth || 720);
          image.height = Number(sourceImage.getAttribute('height') || sourceImage.naturalHeight || 900);
          image.loading = 'lazy';
          image.alt = sourceImage.alt || 'Customer review image';
          target.append(image);
        }

        if (sourceVideoButton) {
          const play = document.createElement('button');
          play.type = 'button';
          play.className = 'featured-reviews-summary__play';
          play.dataset.featuredVideoTrigger = '';
          play.dataset.videoTemplate = sourceVideoButton.dataset.reviewVideo;
          play.setAttribute('aria-label', 'Play review video');
          const icon = document.createElement('span');
          icon.setAttribute('aria-hidden', 'true');
          icon.textContent = '▶';
          play.append(icon);
          target.append(play);
        }
        target.hidden = !sourceImage && !sourceVideoButton;
      }

      populateExistingProduct(card, source) {
        const target = card.querySelector('[data-featured-product]');
        if (!target) return;
        target.replaceChildren();
        target.hidden = true;
        if (this.dataset.showAssociatedProduct !== 'true') return;
        const sourceProduct = source.querySelector('.customer-reviews__product');
        if (!sourceProduct) return;
        const clone = sourceProduct.cloneNode(true);
        clone.className = 'featured-reviews-summary__product';
        target.append(clone);
        target.hidden = false;
      }

      setupTextLimits() {
        this.querySelectorAll('[data-featured-review-text].is-collapsible').forEach((text) => {
          const button = text.parentElement?.querySelector('[data-featured-read-more]');
          if (!button || text.classList.contains('is-expanded')) return;
          button.hidden = text.scrollHeight <= text.clientHeight + 1;
        });
      }

      syncDestination() {
        const link = this.querySelector('[data-featured-view-all]');
        if (!link) return;
        const target = document.querySelector(this.dataset.reviewAnchor || '#customer-reviews');
        if (target) {
          link.hidden = false;
          link.href = this.dataset.reviewAnchor || '#customer-reviews';
          link.dataset.hasReviewTarget = 'true';
        } else if (this.dataset.fallbackUrl) {
          link.hidden = false;
          link.href = this.dataset.fallbackUrl;
          delete link.dataset.hasReviewTarget;
        } else {
          link.hidden = true;
          delete link.dataset.hasReviewTarget;
        }
      }

      handleClick(event) {
        const readMore = event.target.closest('[data-featured-read-more]');
        if (readMore) {
          const text = readMore.parentElement?.querySelector('[data-featured-review-text]');
          const expanded = !text?.classList.contains('is-expanded');
          text?.classList.toggle('is-expanded', expanded);
          readMore.setAttribute('aria-expanded', String(expanded));
          const label = readMore.querySelector('[data-read-more-label]');
          if (label) label.textContent = expanded ? this.dataset.showLessLabel : this.dataset.readMoreLabel;
          return;
        }

        const videoTrigger = event.target.closest('[data-featured-video-trigger]');
        if (videoTrigger) {
          this.openVideo(videoTrigger);
          return;
        }

        if (event.target.closest('[data-featured-dialog-close]') || event.target === this.dialog) {
          this.closeVideo();
          return;
        }

        const previous = event.target.closest('[data-featured-previous]');
        const next = event.target.closest('[data-featured-next]');
        if (previous || next) {
          this.scrollCards(previous ? -1 : 1);
          return;
        }

        const viewAll = event.target.closest('[data-featured-view-all][data-has-review-target="true"]');
        if (viewAll) this.scrollToReviews(event);
      }

      scrollCards(direction) {
        const card = this.cards?.querySelector('[data-featured-card]:not([hidden])');
        if (!card || !this.cards) return;
        const gap = parseFloat(getComputedStyle(this.cards).columnGap || 0);
        this.cards.scrollBy({
          left: direction * (card.getBoundingClientRect().width + gap),
          behavior: this.prefersReducedMotion.matches ? 'auto' : 'smooth',
        });
      }

      scrollToReviews(event) {
        const target = document.querySelector(this.dataset.reviewAnchor || '#customer-reviews');
        if (!target) return;
        event.preventDefault();
        const header = document.querySelector('sticky-header, .section-header.shopify-section-group-header-group');
        const headerHeight = header?.getBoundingClientRect().height || 0;
        const extraOffset = Number(this.dataset.scrollOffset || 0);
        const top = target.getBoundingClientRect().top + window.scrollY - headerHeight - extraOffset;
        history.pushState(null, '', this.dataset.reviewAnchor || '#customer-reviews');
        window.scrollTo({ top: Math.max(0, top), behavior: this.prefersReducedMotion.matches ? 'auto' : 'smooth' });
        window.setTimeout(
          () => target.focus({ preventScroll: true }),
          this.prefersReducedMotion.matches ? 0 : 320
        );
      }

      openVideo(trigger) {
        const templateId = trigger.dataset.videoTemplate;
        const template = templateId ? document.getElementById(templateId) : null;
        if (!template || !this.dialog || !this.dialogMedia || typeof this.dialog.showModal !== 'function') return;
        this.videoTrigger = trigger;
        this.dialogMedia.replaceChildren(template.content.cloneNode(true));
        const iframe = this.dialogMedia.querySelector('iframe');
        if (iframe && !iframe.title) iframe.title = 'Review video';
        document.body.classList.add('featured-reviews-dialog-open');
        this.dialog.showModal();
        this.dialog.querySelector('[data-featured-dialog-close]')?.focus();
        this.dialogMedia.querySelector('video')?.play().catch(() => {});
      }

      closeVideo() {
        if (this.dialog?.open) this.dialog.close();
      }

      handleDialogCancel(event) {
        event.preventDefault();
        this.closeVideo();
      }

      handleDialogClose() {
        this.dialogMedia?.querySelectorAll('video').forEach((video) => video.pause());
        this.dialogMedia?.replaceChildren();
        document.body.classList.remove('featured-reviews-dialog-open');
        const trigger = this.videoTrigger;
        this.videoTrigger = null;
        if (trigger?.isConnected) trigger.focus({ preventScroll: true });
      }

      handleKeydown(event) {
        if (!this.dialog?.open) return;
        if (event.key === 'Escape') {
          event.preventDefault();
          this.closeVideo();
          return;
        }
        if (event.key !== 'Tab') return;
        const focusable = Array.from(
          this.dialog.querySelectorAll(
            'button:not([disabled]), [href], video[controls], [tabindex]:not([tabindex="-1"])'
          )
        ).filter((element) => element.getClientRects().length > 0);
        if (!focusable.length) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    }
  );
}
